/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { GoodDate } from '@utils/datetime';
import { nanoid } from '@utils/crypto';
import { Tag } from '@models/time/tag';
import { ActivityBoardSection, ActivityProject } from '@models/time/project';
import httpRequest from '@utils/http-request';
import { asRecord, asArray, asString, asBoolean, asNumber } from '@models/json-utils';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

export type ActivityStatus = 'todo' | 'ready' | 'wip' | 'review' | 'done';
export const activityStatuses: ActivityStatus[] = ['todo', 'ready', 'wip', 'review', 'done'];
export type ActivityCounterMode = 'none' | 'countdown' | 'countforward';

export enum ActivityType {
    General = 0,
    Work = 1,
    Study = 2,
    Festival = 3,
    Birthday = 4,
}

export interface RepeatRule {
    frequency: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'weekday' | 'weekend' | 'custom';
    interval: number;
    count?: number;
    until?: number;
    byWeekday?: number[];
    byMonthDay?: number[];
    byMonth?: number[];
    useLunar: boolean;
}

export type TimingRangeType = 'none' | 'specific' | 'recurring';

export interface Timing {
    id: string;
    ownerType: 'activity' | 'subtask';
    ownerId: string;
    rangeType: TimingRangeType;
    startTime?: GoodDate;
    endTime?: GoodDate;
    allDay?: boolean;
    recurrence?: RepeatRule;
    createdAt?: GoodDate;
    updatedAt?: GoodDate;
    deletedAt?: GoodDate;
}

export interface ActivitySubtask {
    id: string;
    activityId: string;
    description: string;
    completed: boolean;
    timings: Timing[];
    createdAt?: GoodDate;
    updatedAt?: GoodDate;
    deletedAt?: GoodDate;
}

export interface ActivityReport {
    id: string;
    activityId: string;
    actorUserId?: string;
    action: string;
    resourceType: string;
    resourceId?: string;
    summary: string;
    metadata?: Record<string, unknown>;
    createdAt: GoodDate;
    updatedAt?: GoodDate;
    deletedAt?: GoodDate;
}

export interface ActivityPagination {
    limit: number;
    offset: number;
    count: number;
    total: number;
    hasNextPage: boolean;
}

export interface ActivityListResult {
    activities: Activity[];
    pagination: ActivityPagination;
}

export interface ActivityListProps {
    start?: number;
    end?: number;
    sortBy?: 'updatedAt' | 'startTime';
    sortOrder?: 'asc' | 'desc';
    tags?: string[];
    tagsCsv?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
    page?: number;
}

const epochToGoodDate = (value: unknown): GoodDate | undefined => {
    if (value === undefined || value === null) return undefined;
    return GoodDate.fromEpochSeconds(Number(value));
};

const parseTiming = (raw: unknown): Timing => {
    const r = asRecord(raw);
    return {
        id: asString(r.id),
        ownerType: asString(r.ownerType) as Timing['ownerType'],
        ownerId: asString(r.ownerId),
        rangeType: asString(r.rangeType) as TimingRangeType,
        startTime: epochToGoodDate(r.startTime),
        endTime: epochToGoodDate(r.endTime),
        allDay: asBoolean(r.allDay, false),
        recurrence: (r.recurrence as RepeatRule) ?? undefined,
        createdAt: epochToGoodDate(r.createdAt),
        updatedAt: epochToGoodDate(r.updatedAt),
        deletedAt: epochToGoodDate(r.deletedAt),
    };
};

const parseSubtask = (raw: unknown): ActivitySubtask => {
    const r = asRecord(raw);
    return {
        id: asString(r.id),
        activityId: asString(r.activityId),
        description: asString(r.description, '-'),
        completed: Boolean(r.completed),
        timings: asArray(r.timings ?? [], parseTiming),
        createdAt: epochToGoodDate(r.createdAt),
        updatedAt: epochToGoodDate(r.updatedAt),
        deletedAt: epochToGoodDate(r.deletedAt),
    };
};

const parseActivityReport = (raw: unknown): ActivityReport => {
    const r = asRecord(raw);
    return {
        id: asString(r.id),
        activityId: asString(r.activityId),
        actorUserId: r.actorUserId === undefined || r.actorUserId === null ? undefined : asString(r.actorUserId),
        action: asString(r.action),
        resourceType: asString(r.resourceType),
        resourceId: r.resourceId === undefined || r.resourceId === null ? undefined : asString(r.resourceId),
        summary: asString(r.summary),
        metadata: asRecord(r.metadata ?? {}),
        createdAt: epochToGoodDate(r.createdAt) ?? new GoodDate(),
        updatedAt: epochToGoodDate(r.updatedAt),
        deletedAt: epochToGoodDate(r.deletedAt),
    };
};

const parseActivityPagination = (raw: unknown, count: number): ActivityPagination => {
    const r = asRecord(raw);
    return {
        limit: asNumber(r.limit, count),
        offset: asNumber(r.offset, 0),
        count: asNumber(r.count, count),
        total: asNumber(r.total, count),
        hasNextPage: asBoolean(r.hasNextPage, false),
    };
};

export class Activity {
    id: string;
    name: string;
    description: string;
    priority: number;
    status: ActivityStatus;
    projectId: string;
    sectionId: string;
    counterMode: ActivityCounterMode;
    createdAt: GoodDate;
    updatedAt: GoodDate;
    deletedAt?: GoodDate;

    type: ActivityType;
    repeatRule?: RepeatRule;
    contactId?: string;
    project?: ActivityProject;
    section?: ActivityBoardSection;
    tags: Tag[];
    timings: Timing[];
    subtasks: ActivitySubtask[];

    constructor(
        id: string,
        name: string,
        description: string,
        priority: number,
        status: string,
        projectId: string,
        tags: Tag[],
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        type: ActivityType = ActivityType.General,
        repeatRule?: RepeatRule,
        contactId?: string,
        project?: ActivityProject,
        timings: Timing[] = [],
        subtasks: ActivitySubtask[] = [],
        sectionId = '',
        counterMode: ActivityCounterMode = 'none',
        section?: ActivityBoardSection
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.priority = priority;
        this.status = status as ActivityStatus;
        this.projectId = projectId;
        this.sectionId = sectionId;
        this.counterMode = counterMode;
        this.tags = tags;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt) : undefined;
        this.type = type;
        this.repeatRule = repeatRule;
        this.contactId = contactId;
        this.project = project;
        this.section = section;
        this.timings = timings;
        this.subtasks = subtasks;
    }

    static fromJSON(json: unknown): Activity {
        const j = json as Record<string, unknown>;
        return new Activity(
            j.id as string,
            asString(j.name, ''),
            asString(j.description, ''),
            j.priority as number,
            j.status as string,
            asString(j.projectId, ''),
            j.tags ? Tag.fromJSONList(j.tags) : [],
            j.createdAt as number,
            j.updatedAt as number,
            j.deletedAt as number,
            (j.type as ActivityType) ?? ActivityType.General,
            j.repeatRule as RepeatRule,
            j.contactId as string,
            j.project ? ActivityProject.fromJSON(j.project) : undefined,
            ((j.timings ?? []) as unknown[]).map(parseTiming),
            ((j.subtasks ?? []) as unknown[]).map(parseSubtask),
            asString(j.sectionId, ''),
            (asString(j.counterMode, 'none') as ActivityCounterMode) || 'none',
            j.section ? ActivityBoardSection.fromJSON(j.section) : undefined
        );
    }

    static fromJSONList(json: unknown): Activity[] {
        const array = json as unknown[];
        return array.map((item: unknown) => Activity.fromJSON(item));
    }

    static get template(): Activity {
        const now = GoodDate.nowInEpochSeconds();
        return new Activity(
            nanoid(12),
            '',
            '',
            0,
            'todo',
            '',
            [],
            now,
            now,
            undefined,
            ActivityType.General,
            undefined,
            undefined,
            undefined,
            [],
            []
        );
    }

    static listPage = async (props: ActivityListProps = {}): Promise<ActivityListResult> => {
        const params = new URLSearchParams();
        if (props.start !== undefined) params.set('start', String(props.start));
        if (props.end !== undefined) params.set('end', String(props.end));
        if (props.sortBy) params.set('sortBy', props.sortBy);
        if (props.sortOrder) params.set('sortOrder', props.sortOrder);
        if (props.tags && props.tags.length > 0) props.tags.forEach((t) => params.append('tags', t));
        if (props.tagsCsv) params.set('tagsCsv', props.tagsCsv);
        if (props.projectId) params.set('projectId', props.projectId);
        if (props.limit !== undefined) params.set('limit', String(Math.min(Math.max(props.limit, 1), 100)));
        if (props.offset !== undefined) params.set('offset', String(Math.max(props.offset, 0)));
        if (props.page !== undefined) params.set('page', String(Math.max(props.page, 1)));

        const qs = params.toString();
        const response = await httpRequest(`${API_BASE}/time/activities${qs ? `?${qs}` : ''}`, 'GET');
        const json = await response.json();

        if (response.status === 200) {
            const activities = Activity.fromJSONList(json.data);
            return {
                activities,
                pagination: parseActivityPagination(json.pagination, activities.length),
            };
        }
        throw new Error(json.error);
    };

    static list = async (props: ActivityListProps = {}): Promise<Activity[]> => {
        const result = await Activity.listPage(props);
        return result.activities;
    };

    static listAll = async (props: ActivityListProps = {}): Promise<Activity[]> => {
        const limit = 100;
        let offset = 0;
        const activities: Activity[] = [];

        for (;;) {
            const page = await Activity.listPage({ ...props, limit, offset });
            activities.push(...page.activities);
            if (!page.pagination.hasNextPage || page.activities.length === 0) break;
            offset += page.activities.length;
        }

        return activities;
    };

    static listRecent = async (): Promise<Activity[]> => {
        const response = await httpRequest(`${API_BASE}/time/activities/recent`, 'GET');
        const json = await response.json();

        if (response.status === 200) {
            return Activity.fromJSONList(json.data);
        }
        throw new Error(json.error);
    };

    static listByTag = async (id: string): Promise<Activity[]> => {
        const response = await httpRequest(`${API_BASE}/time/tags/${id}/activities`, 'GET');
        const json = await response.json();

        if (response.status === 200) {
            return Activity.fromJSONList(json.data);
        }
        throw new Error(json.error);
    };

    static get = async (id: string): Promise<Activity> => {
        const response = await httpRequest(`${API_BASE}/time/activities/${id}`, 'GET');
        const json = await response.json();

        if (response.status === 200) {
            return Activity.fromJSON(json.data);
        }
        throw new Error(json.error);
    };

    static listReports = async (id: string): Promise<ActivityReport[]> => {
        const response = await httpRequest(`${API_BASE}/time/activities/${id}/reports`, 'GET');
        const json = await response.json();

        if (response.status === 200) {
            return asArray(json.data, parseActivityReport);
        }
        throw new Error(json.error);
    };

    static save = async (id: string, activity: Activity): Promise<Activity> => {
        const payload = {
            name: activity.name,
            description: activity.description,
            projectId: activity.projectId,
            type: activity.type,
            priority: activity.priority,
            status: activity.status,
            sectionId: activity.sectionId,
            counterMode: activity.counterMode,
            repeatRule: activity.repeatRule,
            contactId: activity.contactId,
        };
        const response = await httpRequest(`${API_BASE}/time/activities/${id}`, 'PUT', payload);
        const json = await response.json();

        if (response.status === 200) {
            return Activity.fromJSON(json.data);
        }
        throw new Error(json.error);
    };

    static delete = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/activities/${id}`, 'DELETE');
        if (response.status !== 200) {
            throw new Error((await response.json()).error);
        }
    };

    static saveTimings = async (
        id: string,
        timings: Array<{
            id?: string;
            rangeType: TimingRangeType;
            startTime?: GoodDate | number;
            endTime?: GoodDate | number;
            allDay?: boolean;
            recurrence?: RepeatRule;
        }>
    ): Promise<{ count: number }> => {
        const payload = timings.map((t) => ({
            id: t.id,
            rangeType: t.rangeType,
            startTime:
                t.startTime === undefined
                    ? undefined
                    : typeof t.startTime === 'number'
                      ? t.startTime
                      : t.startTime.toEpochSeconds(),
            endTime:
                t.endTime === undefined
                    ? undefined
                    : typeof t.endTime === 'number'
                      ? t.endTime
                      : t.endTime.toEpochSeconds(),
            allDay: Boolean(t.allDay),
            recurrence: t.recurrence,
        }));

        const response = await httpRequest(`${API_BASE}/time/activities/${id}/timings`, 'PUT', payload);
        const json = await response.json();
        if (response.status === 200) {
            return json.data as { count: number };
        }
        throw new Error(json.error);
    };

    static deleteTiming = async (timingId: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/activities/timing/${timingId}`, 'DELETE');
        if (response.status !== 200) {
            throw new Error((await response.json()).error);
        }
    };

    static saveSubtasks = async (
        id: string,
        subtasks: Array<{
            id?: string;
            description?: string;
            completed?: boolean;
            timings?: Array<{
                id?: string;
                rangeType: TimingRangeType;
                startTime?: GoodDate | number;
                endTime?: GoodDate | number;
                allDay?: boolean;
                recurrence?: RepeatRule;
            }>;
        }>
    ): Promise<{ count: number }> => {
        const payload = subtasks.map((s) => ({
            id: s.id,
            description: s.description,
            completed: Boolean(s.completed),
            timings: (s.timings ?? []).map((t) => ({
                id: t.id,
                rangeType: t.rangeType,
                startTime:
                    t.startTime === undefined
                        ? undefined
                        : typeof t.startTime === 'number'
                          ? t.startTime
                          : t.startTime.toEpochSeconds(),
                endTime:
                    t.endTime === undefined
                        ? undefined
                        : typeof t.endTime === 'number'
                          ? t.endTime
                          : t.endTime.toEpochSeconds(),
                allDay: Boolean(t.allDay),
                recurrence: t.recurrence,
            })),
        }));

        const response = await httpRequest(`${API_BASE}/time/activities/${id}/subtasks`, 'PUT', payload);
        const json = await response.json();
        if (response.status === 200) {
            return json.data as { count: number };
        }
        throw new Error(json.error);
    };

    static deleteSubtask = async (subtaskId: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/activities/subtask/${subtaskId}`, 'DELETE');
        if (response.status !== 200) {
            throw new Error((await response.json()).error);
        }
    };
}
